import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getLastFinishedCompetitionOptions } from '@/entities/Leaderboard/api/getLastFinishedCompetitionOptions';
import { leaderBoardsKeys } from '@/entities/Leaderboard/api/leaderBoardsKeys';
import { useUserStore } from '@/entities/User/store/userStoreProvider';
import type { TReward } from '../types';
import { useCompetitionFinished } from './useCompetitionFinished';
const DELAYED_REFETCH_TIMEOUT = 10000; // 10 seconds

export const useRewardModal = () => {
  const $ = _c(21);
  const user = useUserStore(_temp);
  const queryClient = useQueryClient();
  const [reward, setReward] = useState();
  const [isOpenRewardModal, setIsOpenRewardModal] = useState(false);
  const delayedRefetchTimerRef = useRef(null);
  const triggeredBySocketRef = useRef(false);
  const presentedLastFinishedCompetitionIdRef = useRef(null);
  const t0 = user?.id;
  let t1;
  if ($[0] !== t0) {
    t1 = getLastFinishedCompetitionOptions(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      ...t1,
      structuralSharing: false,
      staleTime: Infinity,
      gcTime: 60000
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: lastFinishedCompetition,
    dataUpdatedAt,
    refetch
  } = useQuery(t2);
  let t3;
  if ($[4] !== refetch) {
    t3 = () => {
      presentedLastFinishedCompetitionIdRef.current = null;
      triggeredBySocketRef.current = true;
      refetch();
      if (delayedRefetchTimerRef.current) {
        clearTimeout(delayedRefetchTimerRef.current);
      }
      delayedRefetchTimerRef.current = setTimeout(() => refetch(), DELAYED_REFETCH_TIMEOUT);
    };
    $[4] = refetch;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      presentedLastFinishedCompetitionIdRef.current = null;
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3) {
    t5 = {
      onCompetitionFinished: t3,
      onCompetitionStarted: t4
    };
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  useCompetitionFinished(t5);
  const endTime = lastFinishedCompetition?.endTime;
  let t6;
  if ($[9] !== lastFinishedCompetition) {
    t6 = () => {
      if (!lastFinishedCompetition?.endTime) {
        if (!lastFinishedCompetition) {
          presentedLastFinishedCompetitionIdRef.current = null;
        }
        return;
      }
      const {
        competitionId,
        percentileGroup,
        tokensEarned,
        modalAlreadyShown
      } = lastFinishedCompetition;
      if (presentedLastFinishedCompetitionIdRef.current === competitionId) {
        return;
      }
      if (modalAlreadyShown && !triggeredBySocketRef.current) {
        presentedLastFinishedCompetitionIdRef.current = competitionId;
        return;
      }
      triggeredBySocketRef.current = false;
      if (percentileGroup != null && tokensEarned != null && tokensEarned > 0) {
        presentedLastFinishedCompetitionIdRef.current = competitionId;
        setReward({
          poe: tokensEarned,
          place: percentileGroup
        });
        setIsOpenRewardModal(true);
      }
    };
    $[9] = lastFinishedCompetition;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== dataUpdatedAt || $[12] !== endTime || $[13] !== lastFinishedCompetition) {
    t7 = [endTime, dataUpdatedAt, lastFinishedCompetition];
    $[11] = dataUpdatedAt;
    $[12] = endTime;
    $[13] = lastFinishedCompetition;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  if ($[15] !== queryClient) {
    t8 = () => {
      setIsOpenRewardModal(false);
      queryClient.invalidateQueries({
        queryKey: leaderBoardsKeys.lastFinished()
      });
      queryClient.invalidateQueries({
        queryKey: ["user"]
      });
    };
    $[15] = queryClient;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const closeModal = t8;
  let t9;
  if ($[17] !== closeModal || $[18] !== isOpenRewardModal || $[19] !== reward) {
    t9 = {
      reward,
      isOpenRewardModal,
      closeModal
    };
    $[17] = closeModal;
    $[18] = isOpenRewardModal;
    $[19] = reward;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
};
function _temp(state) {
  return state.user;
}